<template>
	<div
		:name="`dropdown-${name}`"
		:class="[
			'dropdown-component',
			{ focused: optionsEnabled, error: hasError }
		]"
	>
		<label v-if="label" :for="`combobox_${name}`">
			<div class="heading-5">
				{{ `${label} ` }}
				<span>{{ ` ${required ? ' *' : ''}` }}</span>
			</div>
		</label>
		<label
			v-else-if="accessibilityLabel"
			:aria-label="accessibilityLabel"
			:for="`combobox_${name}`"
		></label>
		<div class="dropdown-wrapper" :class="size">
			<div :class="['dropdown', { disabled: disabled }]" @click="toggleOptions">
				<input
					:id="`combobox_${name}`"
					ref="customDropdown"
					role="combobox"
					:value="
						value < 0 || !options[value]
							? null
							: options[value].name || options[value].Name
					"
					:name="name"
					readonly="readonly"
					:placeholder="placeholder"
					:disabled="disabled"
					:aria-expanded="optionsEnabled.toString()"
					:aria-owns="`list_${name}`"
					@input="updateValue"
					@blur="onInputBlur"
					@keydown.enter="toggleOptions"
					@keydown.space="toggleOptions"
				/>
				<svgicon
					name="arrow-down"
					class="arrow"
					:class="{ focused: optionsEnabled }"
				></svgicon>
			</div>
			<ul
				v-show="optionsEnabled"
				:id="`list_${name}`"
				class="options-list"
				role="listbox"
				:aria-labelledby="`combobox_${name}`"
				@keydown="onListKeyDown"
			>
				<li
					v-for="(option, index) in options"
					:ref="index"
					:key="index"
					class="option"
					role="option"
					:aria-selected="(value == index).toString()"
					:aria-label="option.name || option.Name"
					tabindex="0"
					:class="{
						selected: value == index
					}"
					@click="selectOption($event, index)"
					@keydown.enter="selectOption($event, index)"
					@keydown.space="selectOption($event, index)"
				>
					{{ option.name || option.Name }}
				</li>
			</ul>
			<div v-show="hasError" class="error-msg">{{ error }}</div>
		</div>
	</div>
</template>
<script>
import '@/components/icons/arrow-down';
import KeyboardNavigation from '@/mixins/KeyboardNavigation';

export default {
	mixins: [KeyboardNavigation],
	props: {
		options: {
			required: true,
			type: Array,
			default: () => {
				return [];
			}
		},
		value: {
			type: Number,
			default: -1
		},
		size: {
			type: String,
			default: 'large',
			validator: value => {
				return ['large', 'mid', 'small', 'slim'].indexOf(value) !== -1;
			}
		},
		name: {
			type: String,
			required: true
		},

		label: {
			type: String,
			default: ''
		},

		required: {
			type: Boolean,
			default: false
		},

		placeholder: {
			type: String,
			default: ''
		},
		validationCheck: {
			type: Boolean,
			default: false
		},
		disabled: {
			type: Boolean,
			default: false
		},
		accessibilityLabel: {
			type: String,
			default: ''
		}
	},
	data() {
		return {
			currentFocusedElem: -1,
			error: null,
			optionsEnabled: false
		};
	},
	computed: {
		hasError() {
			return !!this.error;
		}
	},
	watch: {
		value(newVal) {
			if (newVal) {
				this.error = null;
			}
		},
		validationCheck(value) {
			if (value) {
				this.checkForValue();
			}
		},
		optionsEnabled(value) {
			if (!value) {
				this.checkForValue();
			}
		}
	},
	created() {
		document.addEventListener('click', this.documentClick);
	},
	destroyed() {
		document.removeEventListener('click', this.documentClick);
	},
	methods: {
		onListKeyDown(e) {
			switch (e.keyCode) {
				case this.keyboardKeyEvents.DOWN_ARROW_KEY_CODE:
					e.preventDefault();
					e.stopPropagation();
					this.focusNextItem();

					if (this.currentFocusedElem >= this.options.length) {
						this.closeOptions();
					} else {
						this.focusElement();
					}
					return;

				case this.keyboardKeyEvents.UP_ARROW_KEY_CODE:
					e.preventDefault();
					e.stopPropagation();
					this.focusPrevItem();
					if (this.currentFocusedElem < 0) {
						this.closeOptions();
						this.$refs.customDropdown.focus();
					} else {
						this.focusElement();
					}
					return;

				case this.keyboardKeyEvents.HOME_KEY_CODE:
					e.preventDefault();
					e.stopPropagation();
					this.focusFirstItem();
					this.focusElement();
					return;

				case this.keyboardKeyEvents.END_KEY_CODE:
					e.preventDefault();
					e.stopPropagation();
					this.focusLastItem();
					this.focusElement();
					return;

				case this.keyboardKeyEvents.ESCAPE_KEY_CODE:
					this.closeOptions();
					return;

				case this.keyboardKeyEvents.TAB_KEY_CODE:
					this.closeOptions();
					return;

				default:
					return;
			}
		},
		documentClick() {
			if (!this.$el.contains(event.target)) {
				this.optionsEnabled = false;
			}
		},
		closeOptions() {
			this.optionsEnabled = false;
		},

		focusElement() {
			if (this.currentFocusedElem !== null && this.currentFocusedElem >= 0) {
				this.$refs[this.currentFocusedElem][0].focus();
			}
		},

		toggleOptions() {
			if (!this.disabled) {
				this.optionsEnabled = !this.optionsEnabled;
				this.$refs.customDropdown.focus();
				if (this.optionsEnabled) {
					this.currentFocusedElem =
						this.value !== null && this.value >= 0 ? this.value : 0;
					setTimeout(() => {
						this.focusElement();
					});
				} else {
					this.$refs.customDropdown.focus();
				}
			}
		},

		updateValue() {
			this.error = null;
			this.$emit('input', parseInt(this.$refs.customDropdown.value));
		},

		selectOption(e, option) {
			e.preventDefault();
			e.stopPropagation();
			if (option !== this.value) {
				this.$refs.customDropdown.value = option;
				this.$refs.customDropdown.dispatchEvent(new Event('input'));
			}
			this.optionsEnabled = false;
			this.$refs.customDropdown.focus();
		},

		checkForValue() {
			if (
				this.required &&
				this.$refs.customDropdown &&
				!this.$refs.customDropdown.value
			) {
				this.error = `Please enter ${this.label}`;
			}
		},
		onInputBlur() {
			this.$emit('blur');
		}
	}
};
</script>
<style lang="scss" scoped>
label {
	.heading-5 {
		color: $font-dark;
		margin-bottom: 4px;
		span {
			color: $requiredFieldAsterisk;
		}
	}
}
.dropdown-wrapper {
	position: relative;
	font-family: 'RobotoRegular';
	color: $input-color;
	font-size: 15px;
	line-height: 23px;
	letter-spacing: 0.5px;

	@media (min-width: $breakpoint-mid) {
		font-size: 18px;
		line-height: 27px;
	}

	.dropdown {
		position: relative;

		input {
			border: 1px solid $container-border;
			border-radius: 4px;
			color: $input-color;
			background-color: $input-bgcolor;
			outline: 0;
			font-size: 15px;
			line-height: 23px;
			letter-spacing: 0.5px;
			padding-right: 32px;
			@media (min-width: $breakpoint-mid) {
				font-size: 18px;
				line-height: 27px;
			}

			&:disabled {
				background-color: $disabled-state;
				border: 1px solid $disabled-state;
				color: $disabled-text-color;
				&:hover {
					cursor: not-allowed;
				}
			}

			&:not(:disabled) {
				&:hover {
					cursor: pointer;
				}

				&:focus {
					border-color: $hover-state;
				}
			}
		}

		&.disabled {
			.arrow {
				fill: $font-gray;

				&:hover {
					cursor: not-allowed;
				}
			}
		}

		.arrow {
			position: absolute;
			top: 0;
			bottom: 0;
			right: 0;
			margin: auto;
			width: 24px;
			height: 24px;
			fill: $icon-color;
			&:hover {
				cursor: pointer;
			}
			&.focused {
				fill: $hover-state;
			}
		}
	}

	&.large {
		input {
			height: 48px;
			line-height: 48px;
			width: 100%;
			padding-left: 16px;
		}
		.arrow {
			right: 8px;
		}
	}
	&.mid {
		input {
			height: 48px;
			line-height: 48px;
			width: 240px;
			padding-left: 16px;
		}
		.arrow {
			right: 8px;
		}
	}
	&.small {
		input {
			height: 32px;
			line-height: 32px;
			width: 84px;
			text-align: center;
		}
		.arrow {
			right: 4px;
		}
	}

	&.slim {
		font-size: 14px;
		input {
			height: 28px;
			line-height: 28px;
			width: 100%;
			text-align: right;
			border: none;
			font-size: 14px;
			font-family: 'RobotoBold';
		}

		.options-list {
			.option {
				min-height: 24px;
				line-height: 24px;
				padding-left: 16px;
				list-style-type: none;
			}
		}
	}

	.options-list {
		border: 1px solid $container-border;
		border-top: 0;
		border-radius: 4px;
		position: absolute;
		background-color: #ffffff;
		z-index: 2;
		width: 100%;
		max-height: 200px;
		overflow-y: auto;
		box-shadow: 0 15px 30px 0 rgba(0, 76, 208, 0.24);
		padding: 0;
		margin: 0;

		.option {
			min-height: 40px;
			line-height: 40px;
			padding-left: 16px;
			list-style-type: none;

			&.selected {
				background-color: $selected-option-bgcolor;
			}

			&.focused {
				background-color: $selected-option-bgcolor;
			}

			&:hover {
				cursor: pointer;
				background-color: $hover-option-bgcolor;
			}
		}
	}
}
.error {
	.dropdown-wrapper {
		input {
			border-color: $error-state;
		}
	}

	label {
		h5 {
			color: $error-state;
		}
	}
}

.error-msg {
	font-family: 'RobotoRegular';
	padding-top: 8px;
	line-height: 22px;
	color: $error-state;
	font-size: 14px;
	letter-spacing: 0.25px;
	line-height: 21px;
	@media (min-width: $breakpoint-mid) {
		font-size: 16px;
		letter-spacing: 0.5px;
		line-height: 24px;
	}
}
</style>
